<template>
    <LoadingComponent :props="loading" />

    <div id="aiImportFileModal" class="modal ai-import-centered-modal">
        <div class="modal-dialog">
            <div class="modal-header">
                <h3 class="modal-title">{{ $t('button.new_import') }}</h3>
                <button class="modal-close fa-solid fa-xmark text-xl text-slate-400 hover:text-red-500" @click="resetAll"></button>
            </div>
            <div class="modal-body">
                <form @submit.prevent="previewColumns">
                    <div class="form-row">
                        <div class="form-col-12">
                            <p class="db-field-alert m-0 p-0 text-gray-600 text-xs">
                                {{ $t('message.ai_import_instructions') }}
                            </p>
                            <div class="mt-2 flex items-center justify-between text-xs">
                                <a href="/admin/product/ai-import/download-attachment" download="PlantillaImportacionAiProductos.xlsx" class="text-primary hover:underline font-medium inline-flex items-center gap-1">
                                    <i class="fa-solid fa-file-excel text-green-600"></i>
                                    <span>Descargar plantilla de ejemplo para importación con IA</span>
                                </a>
                            </div>
                        </div>

                        <div class="form-col-12">
                            <label class="db-field-title required">{{ $t('label.upload_file') }} (csv/xls/xlsx)</label>
                            <input
                                @change="changeFile"
                                v-bind:class="errors.file ? 'invalid' : ''"
                                id="aiImportFile"
                                type="file"
                                class="db-field-control"
                                ref="fileProperty"
                                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                            />
                            <small class="db-field-alert" v-if="errors.file">{{ errors.file[0] }}</small>
                        </div>

                        <div class="form-col-12 border-t pt-0 mt-3">
                            <div class="modal-btns">
                                <button type="button" class="modal-btn-outline hover:opacity-80 modal-close" @click="resetAll">
                                    <i class="lab lab-fill-close-circle"></i>
                                    <span>{{ $t('button.cancel') }}</span>
                                </button>

                                <button type="submit" class="db-btn py-2 text-white bg-primary hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed" :disabled="!file">
                                    <i class="lab lab-fill-save"></i>
                                    <span>{{ $t('button.upload_file') }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>

    <div id="aiImportMappingModal" class="modal ai-import-fullscreen-modal">
        <div class="modal-dialog">
            <div class="modal-header">
                <h3 class="modal-title">{{ $t('menu.ai_imports') }}</h3>
                <button class="modal-close fa-solid fa-xmark text-xl text-slate-400 hover:text-red-500" @click="resetAll"></button>
            </div>
            <div class="modal-body">
                <form @submit.prevent="save" class="w-full">
                    <div class="form-row">
                        <div class="form-col-12">
                            <p class="db-field-alert max-w-3xl m-0 p-0 text-amber-500 text-sm">
                                {{ $t('message.ai_import_mapping_instructions') }}
                            </p>
                        </div>

                        <div class="form-col-12 sm:form-col-6 lg:form-col-4" v-for="field in mappingFields" :key="field.key">
                            <label class="db-field-title" :class="field.required ? 'required' : ''">
                                {{ field.label }}
                            </label>

                            <vue-select
                                class="db-field-control f-b-custom-select"
                                v-model="columnMapping[field.key]"
                                :options="columnOptions"
                                label-by="name"
                                value-by="id"
                                :closeOnSelect="true"
                                :searchable="true"
                                :clearOnClose="true"
                                :placeholder="$t('label.select_one')"
                                :search-placeholder="$t('label.search_here')"
                                :class="mappingErrors[field.key] ? 'invalid' : ''"
                            />

                            <small class="db-field-alert" v-if="mappingErrors[field.key]">{{ mappingErrors[field.key] }}</small>
                        </div>

                        <div class="form-col-12 mt-2">
                            <div class="db-card p-3 border border-gray-200 rounded-lg bg-gray-50">
                                <div class="flex items-center justify-between mb-2">
                                    <h4 class="db-card-title !mb-0">{{ $t('label.preview') }} <small>({{ $t('message.ai_import_preview_all_viable') }})</small></h4>
                                    <button type="button" class="db-btn py-1.5 px-3 text-white bg-primary hover:opacity-80" @click="loadMappedPreview">
                                        {{ $t('button.refresh_preview') }}
                                    </button>
                                </div>

                                <p class="db-field-alert mb-2 text-gray-600" v-if="previewMissingRequired.length">
                                    {{ $t('message.ai_import_missing_required_fields', { fields: previewMissingRequired.join(', ') }) }}
                                </p>

                                <p class="db-field-alert mb-2 text-amber-600" v-if="previewDiscardedCount">
                                    {{ $t('message.ai_import_discarded_detected', { count: previewDiscardedCount }) }}
                                </p>

                                <div class="db-table-responsive" v-if="previewRows.length">
                                    <table class="db-table stripe">
                                        <thead class="db-table-head">
                                            <tr class="db-table-head-tr">
                                                <th class="db-table-head-th">#</th>
                                                <th class="db-table-head-th">{{ $t('label.sku') }}</th>
                                                <th class="db-table-head-th">{{ $t('label.name') }}</th>
                                                <th class="db-table-head-th">{{ $t('label.buying_price_alt') }}</th>
                                                <th class="db-table-head-th">{{ $t('label.selling_price_alt') }}</th>
                                                <th class="db-table-head-th">{{ $t('label.wholesale_price_alt') }}</th>
                                                <th class="db-table-head-th">{{ $t('label.brand') }}</th>
                                                <th class="db-table-head-th">{{ $t('label.unit') }}</th>
                                                <th class="db-table-head-th">{{ $t('menu.stock') }}</th>
                                            </tr>
                                        </thead>
                                        <tbody class="db-table-body">
                                            <tr class="db-table-body-tr" v-for="row in previewRows" :key="row.line_number">
                                                <td class="db-table-body-td">
                                                    <div class="flex flex-col gap-1">
                                                        <span class="px-2 py-0.5 rounded-full" :class="{'bg-orange-500 text-white w-fit': isRowManuallyEdited(row)}">
                                                            {{ row.line_number }}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td class="db-table-body-td">{{ row.sku }}</td>
                                                <td class="db-table-body-td">{{ row.name }}</td>
                                                <td class="db-table-body-td">{{ row.buying_price }}</td>
                                                <td class="db-table-body-td">{{ row.selling_price }}</td>
                                                <td class="db-table-body-td">{{ row.wholesale_price ?? '--' }}</td>
                                                <td class="db-table-body-td min-w-[220px]">
                                                    <vue-select
                                                        class="db-field-control f-b-custom-select"
                                                        v-model="row.product_brand_id"
                                                        :options="brandOptions"
                                                        label-by="name"
                                                        value-by="id"
                                                        :closeOnSelect="true"
                                                        :searchable="true"
                                                        :clearOnClose="true"
                                                        :placeholder="$t('label.select_one')"
                                                        :search-placeholder="$t('label.search_here')"
                                                    />
                                                </td>
                                                <td class="db-table-body-td min-w-[220px]">
                                                    <vue-select
                                                        class="db-field-control f-b-custom-select"
                                                        v-model="row.unit_id"
                                                        :options="unitOptions"
                                                        label-by="name"
                                                        value-by="id"
                                                        :closeOnSelect="true"
                                                        :searchable="true"
                                                        :clearOnClose="true"
                                                        :placeholder="$t('label.select_one')"
                                                        :search-placeholder="$t('label.search_here')"
                                                    />
                                                </td>
                                                <td class="db-table-body-td">{{ row.stock_quantity }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <p class="db-field-alert mb-0 text-gray-600" v-else-if="!previewMissingRequired.length">
                                    {{ $t('message.ai_import_no_viable_rows') }}
                                </p>
                            </div>
                        </div>

                        <div class="form-col-12 border-t pt-0 mt-2">
                            <div class="modal-btns">
                                <button type="button" class="modal-btn-outline hover:opacity-80" @click="resetAll">
                                    <i class="lab lab-fill-close-circle"></i>
                                    <span>{{ $t('button.cancel') }}</span>
                                </button>

                                <button type="button" class="modal-btn-outline hover:opacity-80" @click="backToUpload">
                                    <i class="lab lab-line-long-arrow-left"></i>
                                    <span>{{ $t('button.previous') }}</span>
                                </button>

                                <button type="submit" class="db-btn py-2 text-white bg-primary hover:opacity-80">
                                    <i class="lab lab-fill-save"></i>
                                    <span>{{ $t('button.continue') }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import LoadingComponent from '../../components/LoadingComponent.vue';
import alertService from '../../../../services/alertService';
import appService from '../../../../services/appService';

export default {
    name: 'AiImportFileUploadComponent',
    components: { LoadingComponent },
    emits: ['created'],
    data() {
        return {
            loading: {
                isActive: false,
            },
            file: null,
            errors: {},
            mappingErrors: {},
            columnOptions: [],
            previewRows: [],
            brandOptions: [],
            unitOptions: [],
            initialRowValues: {},
            previewMissingRequired: [],
            previewDiscardedCount: 0,
            columnMapping: {
                sku: null,
                name: null,
                buying_price: null,
                selling_price: null,
                wholesale_price: null,
                product_brand_id: null,
                unit_id: null,
                stock: null,
            },
            mappingFields: [
                { key: 'sku', label: this.$t('label.sku'), required: true },
                { key: 'name', label: this.$t('label.name'), required: true },
                { key: 'buying_price', label: this.$t('label.buying_price'), required: true },
                { key: 'selling_price', label: this.$t('label.selling_price'), required: true },
                { key: 'wholesale_price', label: this.$t('label.wholesale_price'), required: false },
                { key: 'product_brand_id', label: this.$t('label.brand'), required: false },
                { key: 'unit_id', label: this.$t('menu.unit'), required: false },
                { key: 'stock', label: this.$t('menu.stock'), required: false },
            ],
            search: {
                paginate: 1,
                page: 1,
                per_page: 10,
                order_column: 'id',
                order_type: 'desc',
            },
        };
    },
    methods: {
        resetAll() {
            appService.modalHide('#aiImportFileModal');
            appService.modalHide('#aiImportMappingModal');
            this.file = null;
            this.errors = {};
            this.mappingErrors = {};
            this.columnOptions = [];
            this.previewRows = [];
            this.brandOptions = [];
            this.unitOptions = [];
            this.initialRowValues = {};
            this.previewMissingRequired = [];
            this.previewDiscardedCount = 0;
            this.columnMapping = {
                sku: null,
                name: null,
                buying_price: null,
                selling_price: null,
                wholesale_price: null,
                product_brand_id: null,
                unit_id: null,
                stock: null,
            };
            if (this.$refs.fileProperty) {
                this.$refs.fileProperty.value = null;
            }
        },
        backToUpload() {
            appService.modalHide('#aiImportMappingModal');
            appService.modalShow('#aiImportFileModal');
        },
        changeFile(e) {
            const selectedFile = e?.target?.files?.[0] || null;
            this.file = null;
            this.errors = {};

            if (!selectedFile) {
                return;
            }

            const fileName = String(selectedFile.name || '').toLowerCase();
            const validExtension = ['.csv', '.xls', '.xlsx'].some((ext) => fileName.endsWith(ext));

            if (!validExtension) {
                this.errors = {
                    file: ['Formato de archivo no permitido. Usa csv, xls o xlsx.'],
                };

                if (this.$refs.fileProperty) {
                    this.$refs.fileProperty.value = null;
                }
                return;
            }

            const maxBytes = 20 * 1024 * 1024;
            if ((selectedFile.size || 0) > maxBytes) {
                this.errors = {
                    file: ['El archivo supera el tamaño máximo permitido de 20MB.'],
                };

                if (this.$refs.fileProperty) {
                    this.$refs.fileProperty.value = null;
                }
                return;
            }

            this.file = selectedFile;
        },
        buildPreviewFormData() {
            const fd = new FormData();
            if (this.file) {
                fd.append('file', this.file);
            }

            Object.keys(this.columnMapping).forEach((field) => {
                if (this.columnMapping[field]) {
                    fd.append(`column_mapping[${field}]`, this.columnMapping[field]);
                }
            });

            this.previewRows.forEach((row) => {
                if (!row?.line_number) {
                    return;
                }

                if (row.product_brand_id) {
                    fd.append(`row_overrides[${row.line_number}][product_brand_id]`, row.product_brand_id);
                }

                if (row.unit_id) {
                    fd.append(`row_overrides[${row.line_number}][unit_id]`, row.unit_id);
                }
            });

            return fd;
        },
        applyPreviewData(data) {
            const incomingRows = data.preview_rows || [];
            this.previewRows = incomingRows.map((row) => {
                const line = Number(row.line_number);

                if (line && !this.initialRowValues[line]) {
                    this.initialRowValues[line] = {
                        product_brand_id: row.product_brand_id ?? null,
                        unit_id: row.unit_id ?? null,
                    };
                }

                return {
                    ...row,
                    product_brand_id: row.product_brand_id ?? null,
                    unit_id: row.unit_id ?? null,
                };
            });
            this.brandOptions = data.brand_options || [];
            this.unitOptions = data.unit_options || [];
            this.previewMissingRequired = data.missing_required_fields || [];
            this.previewDiscardedCount = (data.discarded_rows || []).length;
        },
        isRowManuallyEdited(row) {
            const line = Number(row?.line_number || 0);
            if (!line || !this.initialRowValues[line]) {
                return false;
            }

            const initial = this.initialRowValues[line];
            return Number(initial.product_brand_id || 0) !== Number(row.product_brand_id || 0)
                || Number(initial.unit_id || 0) !== Number(row.unit_id || 0);
        },
        applyResolvedMapping(columns, resolvedMapping) {
            const allowedFields = ['sku', 'name', 'buying_price', 'selling_price', 'wholesale_price', 'product_brand_id', 'unit_id', 'stock'];
            const mapped = {};

            allowedFields.forEach((field) => {
                mapped[field] = resolvedMapping?.[field] ?? null;
            });

            this.columnMapping = {
                ...this.columnMapping,
                ...mapped,
            };
        },
        loadMappedPreview() {
            const fd = this.buildPreviewFormData();

            this.loading.isActive = true;
            this.$store.dispatch('aiImport/preview', {
                form: fd,
            }).then((res) => {
                this.loading.isActive = false;
                const data = res.data?.data || {};
                const columns = data.columns || [];
                const resolvedMapping = data.mapping || {};
                this.columnOptions = [
                    { id: null, name: this.$t('label.select_one') },
                    ...columns.map((column) => ({ id: column, name: column })),
                ];
                this.$nextTick(() => {
                    this.applyResolvedMapping(columns, resolvedMapping);
                });
                this.applyPreviewData(data);
            }).catch((err) => {
                this.loading.isActive = false;
                if (err.response?.data?.message) {
                    alertService.error(err.response.data.message);
                }
            });
        },
        previewColumns() {
            try {
                if (!this.file) {
                    this.errors = {
                        file: [this.$t('message.field_required')],
                    };
                    return;
                }

                const fd = new FormData();
                if (this.file) {
                    fd.append('file', this.file);
                }

                this.loading.isActive = true;
                this.$store.dispatch('aiImport/preview', {
                    form: fd,
                }).then((res) => {
                    const data = res.data?.data || {};
                    const columns = data.columns || [];
                    const resolvedMapping = data.mapping || data.suggested_mapping || {};

                    this.columnOptions = [
                        { id: null, name: this.$t('label.select_one') },
                        ...columns.map((column) => ({ id: column, name: column })),
                    ];

                    this.$nextTick(() => {
                        this.applyResolvedMapping(columns, resolvedMapping);
                    });

                    this.applyPreviewData(data);

                    this.loading.isActive = false;
                    this.mappingErrors = {};
                    appService.modalHide('#aiImportFileModal');
                    appService.modalShow('#aiImportMappingModal');
                }).catch((err) => {
                    this.loading.isActive = false;
                    this.errors = err.response?.data?.errors || {};
                    if (!Object.keys(this.errors).length && err.response?.data?.message) {
                        alertService.error(err.response.data.message);
                    }
                });
            } catch (err) {
                this.loading.isActive = false;
                alertService.error(err);
            }
        },
        save() {
            this.mappingErrors = {};
            const requiredFields = ['sku', 'name', 'buying_price', 'selling_price'];
            requiredFields.forEach((field) => {
                if (!this.columnMapping[field]) {
                    this.mappingErrors[field] = this.$t('message.field_required');
                }
            });

            if (this.previewMissingRequired.length) {
                this.previewMissingRequired.forEach((field) => {
                    this.mappingErrors[field] = this.$t('message.field_required');
                });
            }

            if (Object.keys(this.mappingErrors).length) {
                return;
            }

            try {
                const fd = this.buildPreviewFormData();

                this.loading.isActive = true;
                this.$store.dispatch('aiImport/create', {
                    form: fd,
                    search: this.search,
                }).then((res) => {
                    this.loading.isActive = false;

                    const discardedRows = res.data?.meta?.discarded_rows || [];
                    if (discardedRows.length) {
                        const preview = discardedRows
                            .slice(0, 8)
                            .map((row) => `${this.$t('label.line')} ${row.line_number}: ${row.reason}`)
                            .join(' | ');

                        alertService.warning(this.$t('message.ai_import_discarded_warning', {
                            count: discardedRows.length,
                            details: preview,
                        }));
                    }

                    alertService.successFlip(0, this.$t('label.ai_imports_list_loaded'));
                    this.$emit('created', res.data.data);
                    this.resetAll();
                }).catch((err) => {
                    this.loading.isActive = false;
                    this.errors = err.response?.data?.errors || {};
                    if (!Object.keys(this.errors).length && err.response?.data?.message) {
                        alertService.error(err.response.data.message);
                    }
                });
            } catch (err) {
                this.loading.isActive = false;
                alertService.error(err);
            }
        },
    },
};
</script>

<style>
@media (min-width: 1024px) {
    .ai-import-centered-modal {
        display: flex;
        align-items: center;
        justify-content: center;
        left: 260px;
        width: calc(100% - 260px);
        transform: none;
    }

    .ai-import-centered-modal.active {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .ai-import-centered-modal .modal-dialog {
        transform: translateY(-50px);
        margin-top: auto !important;
        margin-bottom: auto !important;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
    }

    .ai-import-centered-modal.active .modal-dialog {
        transform: translateY(0);
    }

    .ai-import-fullscreen-modal {
        left: 260px;
        width: calc(100% - 260px);
        transform: none;
    }
}

.ai-import-fullscreen-modal {
    display: flex;
    align-items: end;
    justify-content: center;
}

.ai-import-fullscreen-modal .modal-dialog {
    width: calc(100% - 40px);
    height: calc(100% - 120px);
    max-width: 100%;
    max-height: 100%;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    margin-top: auto !important;
    margin-bottom: 20px !important;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

.ai-import-fullscreen-modal .modal-body {
    flex: 1;
    overflow: auto;
}
</style>
